'use client';

import { useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CircleAlert, CircleCheck } from 'lucide-react';
import { Button } from '@/shared/ui';
import { cn } from '@/shared/utils/cn';
import { FormShell } from '../FormShell';
import { SubmitButton } from '../SubmitButton';
import type { ConfigurableFormWidgetProps } from './form-widget.types';
import { ConfigurableFormFields } from './ConfigurableFormFields';

import {
  AccountSummary,
  FormCountdownBanner,
  FormFooter,
  FormHeader,
  FormSteps,
  LoggedInDescription,
} from './FormPresentation';
import { useConfigurableForm } from './useConfigurableForm';

export function ConfigurableFormWidget({
  config,
  submitUrl,
  profileUrl,
  termsUrl,
  countdownDeadline,
  countdownText,
  source,
  trackingId,
  pageUrl,
  prefillValues,
  editablePrefill,
  extraFields,
  selectedValues,
  scenario,
  previewSuccess = false,
  mockResult = 'success',
  requireSubmitUrl = false,
  titleAlign,
  formTitle,
  submitAlign,
  buttonWidth,
  recaptchaSiteKey,
  successRedirectUrl,
  successMessage,
  naked,
  stickyFooter,
  onSuccess,
  onError,
  onSubmitPayload,
  onValidationError,
}: ConfigurableFormWidgetProps) {
  const form = useConfigurableForm({
    config,
    mockResult,
    onError,
    onSubmitPayload,
    onSuccess,
    onValidationError,
    pageUrl,
    prefillValues,
    editablePrefill,
    extraFields,
    selectedValues,
    recaptchaSiteKey,
    requireSubmitUrl,
    scenario,
    source,
    submitUrl,
    successRedirectUrl,
    trackingId,
  });

  const shouldReduceMotion = useReducedMotion();
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (form.status === 'error') {
      errorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }
  }, [form.status]);

  // Al cambiar de paso el foco va al título: quien usa lector de pantalla se
  // entera de dónde quedó, y quien usa teclado sigue tabulando desde ahí.
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const hasSteps = Boolean(form.steps);
  // En el primer render no: robar el foco al cargar la página desorienta y
  // deja el título con anillo de foco sin que nadie haya hecho nada.
  const previousStepIndex = useRef(form.currentStepIndex);
  useEffect(() => {
    if (!hasSteps || previousStepIndex.current === form.currentStepIndex) return;
    previousStepIndex.current = form.currentStepIndex;
    stepHeadingRef.current?.focus();
  }, [form.currentStepIndex, hasSteps]);

  const effectiveSubmitAlign = submitAlign ?? config.submit.align ?? 'full';
  const effectiveButtonWidth = buttonWidth ?? (effectiveSubmitAlign === 'full' ? 'full' : 'auto');
  // Los CTA de un formulario por pasos ocupan el ancho: uno solo entero, dos a
  // mitad y mitad, y apilados cuando el formulario es angosto. La barra pegada
  // de un modal es otra cosa y conserva sus botones compactos a la derecha.
  const spreadCtas = Boolean(form.steps) && !stickyFooter;
  const privacyLinkUrl = termsUrl ?? config.privacy.linkUrl;

  const effectiveSuccessMessage = successMessage ?? config.messages.success.message;

  if (previewSuccess || form.status === 'success') {
    return (
      <FormShell naked={naked}>
        <div
          role="status"
          aria-live="polite"
          className="flex flex-col items-center gap-3 py-8 text-center"
        >
          {/* El check entra con un pulso: cierra el golfo de evaluación y hace
              que el envío se sienta terminado, no solo aceptado. */}
          <motion.span
            className="relative flex h-16 w-16 items-center justify-center"
            initial={shouldReduceMotion ? false : { scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 340, damping: 18 }}
          >
            {/* El halo se abre y vuelve a cerrarse: queda como el disco suave
                detrás del check, no se disuelve. */}
            <motion.span
              aria-hidden="true"
              className="bg-adipa-success absolute inset-0 rounded-full opacity-15"
              initial={shouldReduceMotion ? false : { scale: 0.7 }}
              animate={{ scale: shouldReduceMotion ? 1 : [0.7, 1.35, 1] }}
              transition={{ duration: 0.75, times: [0, 0.55, 1], ease: 'easeOut' }}
            />
            <CircleCheck className="text-adipa-success relative h-12 w-12" aria-hidden="true" />
          </motion.span>
          <h2 className="text-adipa-text-primary text-lg font-semibold">
            {config.messages.success.title}
          </h2>
          {effectiveSuccessMessage && (
            <p className="text-adipa-text-secondary text-sm leading-relaxed">
              {effectiveSuccessMessage}
            </p>
          )}
        </div>
      </FormShell>
    );
  }

  return (
    <FormShell naked={naked}>
      <FormCountdownBanner
        deadline={countdownDeadline}
        text={countdownText ?? config.banner?.title}
      />

      <FormHeader
        title={formTitle ?? config.header.title}
        description={
          form.loggedInMessage ? (
            <LoggedInDescription message={form.loggedInMessage} firstName={form.firstName} />
          ) : (
            config.header.description
          )
        }
        align={titleAlign ?? config.header.align}
      />

      {form.isLoggedInComplete && form.accountSummaryRows.length > 0 && (
        <AccountSummary rows={form.accountSummaryRows} />
      )}

      {form.status === 'error' && (
        <div
          ref={errorRef}
          role="alert"
          className="border-adipa-error-border bg-adipa-error-surface text-adipa-error rounded-adipa-md mb-4 flex items-start gap-2 border p-3 text-sm"
        >
          <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            {form.serverErrorMessage ? (
              form.serverErrorMessage
            ) : (
              <>
                <span className="font-semibold">{config.messages.error.title}</span>
                {config.messages.error.message && <> {config.messages.error.message}</>}
              </>
            )}
          </span>
        </div>
      )}

      <form
        onSubmit={form.handleFormSubmit}
        noValidate
        aria-labelledby={form.steps ? `${config.formId}-step-title` : undefined}
      >
        {form.steps && (
          <FormSteps
            currentIndex={form.currentStepIndex}
            headingId={`${config.formId}-step-title`}
            headingRef={stepHeadingRef}
            steps={form.steps}
          />
        )}

        {form.hasEditableFields && (
          <div className="mb-4">
            <h3 className="text-adipa-text-primary text-base font-semibold">
              Completa los datos faltantes
            </h3>
          </div>
        )}

        <ConfigurableFormFields
          accountFieldNames={form.accountFieldNames}
          config={config}
          control={form.control}
          effectiveValues={form.effectiveValues}
          errors={form.errors}
          formFields={form.formFields}
          isLoggedInScenario={form.isLoggedInScenario}
          privacyLinkUrl={privacyLinkUrl}
          profileUrl={profileUrl}
          register={form.register}
          showPrivacy={form.isLastStep}
        />

        <FormFooter
          compactTopSpacing={form.isLoggedInScenario}
          sticky={stickyFooter}
          requiredFieldsLabel={
            form.shouldShowRequiredFieldsLabel ? config.messages.requiredFieldsLabel : undefined
          }
        >
          <div
            className={cn(
              'flex gap-3',
              spreadCtas && 'w-full flex-col @sm:flex-row',
              !form.steps && effectiveSubmitAlign === 'center' && 'justify-center',
              !form.steps && effectiveSubmitAlign === 'left' && 'justify-start',
              form.steps && !spreadCtas && 'ml-auto justify-end',
            )}
          >
            {form.steps && !form.isFirstStep && (
              <Button
                type="button"
                variant="outline"
                size="lg"
                className={spreadCtas ? 'w-full @sm:flex-1' : 'w-auto min-w-28'}
                onClick={form.goToPreviousStep}
              >
                Atrás
              </Button>
            )}

            {form.isLastStep ? (
              <SubmitButton
                loading={form.status === 'validating' || form.status === 'submitting'}
                disabled={!form.isSubmittable || form.status === 'submitting'}
                label={config.submit.label}
                className={
                  spreadCtas
                    ? 'w-full @sm:flex-1'
                    : form.steps
                      ? 'w-auto min-w-44'
                      : effectiveButtonWidth === 'full'
                        ? 'w-full'
                        : 'w-auto min-w-44'
                }
              />
            ) : (
              // Es `submit` para que Enter avance igual que el botón; el hook
              // corta el envío mientras queden pasos.
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className={spreadCtas ? 'w-full @sm:flex-1' : 'w-auto min-w-44'}
                disabled={!form.isCurrentStepValid}
              >
                Continuar
              </Button>
            )}
          </div>
        </FormFooter>
      </form>
    </FormShell>
  );
}
